<template>
  <div v-cloak
      class="base-table-list">
    <slot name="title" />
    <el-table
        ref="table"
        :data="displayItems"
        :default-sort="defaultSort"
        :empty-text="emptyText"
        :height="height"
        :highlight-current-row="highlightCurrentRow"
        :row-class-name="rowClassName"
        :row-key="rowKey"
        :span-method="spanMethod"
        :cell-class-name="cellClassName"
        :cell-style="cellStyle"
        @row-click="onRowClick"
        @row-dblclick="onRowDoubleClick"
        @sort-change="onSortChange"
        @select-all="onSelectAll"
        @selection-change="adjustmentAllSelectCheckBox">
      <el-table-column
          v-if="showSelection"
          type="selection"
          :reserve-selection="true"
          :selectable="canSelectRow"
          width="55"
          class-name="base-table-list-selection tblChkBox" />
      <el-table-column
          v-if="showIndexColumn"
          type="index"
          label="No"
          width="80" />
      <template v-for="(tList, index) in dispTableSetList">
        <template
            v-if="isDisplay(tList)">
          <el-table-column
              :key="index"
              :prop="getPropertyName(tList)"
              :formatter="tList.formatter"
              :sortable="sortable(tList)"
              :sort-method="sortMethod(tList)"
              :sort-by="tList.sortBy"
              :filters="filters(tList)"
              :filter-method="filterMethod(tList)"
              :render-header="tList.renderHeader"
              :fixed="tList.fixed"
              :label="tList.label"
              :width="tList.width"
              :min-width="minColumn(tList)"
              :class-name="tList.class"
              :column-key="getColumnKey(tList)">
            <template v-if="isShowCell(x, tList)"
                slot-scope="x">
              <template v-if="tList.type==='Slot'">
                <slot
                    :name="getPropertyName(tList)"
                    :row="x" />
              </template>
              <template v-else>
                <keep-alive>
                  <BaseCell
                      :name="index"
                      :properties="tList"
                      :display-item="x.row"
                      :editable="editable[tList.editableId]"
                      :validate-scope="validateScope"
                      class="inner-cell" />
                </keep-alive>
              </template>
            </template>
          </el-table-column>
        </template>
      </template>
    </el-table>
  </div>
</template>

<style scoped>

.base-table-list ::v-deep .base-table-list-selection .el-checkbox__input > span {
  background-color: white;
}

.base-table-list ::v-deep .base-table-list-selection .el-checkbox__input.is-disabled > span {
  background-color: #DDDDDD;
}

.search {
  display: flex;
}

</style>

<script>
import BaseCell from '@/components/molecules/BaseCell'
import { v4 as uuid } from 'uuid'
import _ from 'lodash'
import compare from '@/utils/compareUtils'

export default {
  name: 'BaseTableList',
  components: {
    BaseCell
  },
  inject: ['parentValidator'],
  props: {
    showSelection: {
      type: Boolean,
      default: false
    },
    storePath: {
      type: String,
      default: ''
    },
    showIndexColumn: {
      type: Boolean,
      default: false
    },
    tableSetList: {
      type: Array,
      required: true,
      default: () => {
        return []
      }
    },
    displayItems: {
      type: Array,
      required: true,
      default: () => {
        return []
      }
    },
    editable: {
      type: Object,
      default: () => {
        return {}
      }
    },
    defaultSort: {
      type: Object,
      default: () => {
        return {}
      }
    },
    emptyText: {
      type: String,
      default: ''
    },
    minColumnWidth: {
      type: [String, Number],
      default: null
    },
    height: {
      type: [String, Number],
      default: null
    },
    highlightCurrentRow: {
      type: Boolean,
      default: false
    },
    rowClassName: {
      type: [ String, Function ],
      default: ''
    },
    cellClassName: {
      type: [ String, Function ],
      default: ''
    },
    cellStyle: {
      type: Function,
      default: null
    },
    validateScope: {
      type: String,
      default: null
    },
    spanMethod: {
      type: Function,
      default: null
    },
    canSelectMethod: {
      type: Function,
      default: null
    },
    rowKey: {
      type: String,
      default: ''
    },
    asyncRender: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      renderingRowCount: 10,
      oldList: []
    }
  },
  asyncComputed: {
    async dispTableSetList () {
      return this.tableSetList.filter((tList) => this.isShow(tList))
    }
  },
  watch: {
    displayItems: {
      handler: function (val, oldVal) {
        this.adjustmentAllSelectCheckBox()
        if (this.asyncRender) {
          this.rowCountUp(this.renderingRowCount)
          this.renderingRowCount = 10
        }
      },
      deep: true
    },
    tableSetList: {
      handler: function (val, oldVal) {
        if (this.asyncRender) {
          this.rowCountUp(this.renderingRowCount)
          this.renderingRowCount = 10
        }
        this.oldList = oldVal
      },
      deep: true
    }
  },
  created () {
    this.$validator = this.parentValidator
    this.oldList = this.tableSetList
  },
  methods: {
    isShowCell (x, tList) {
      if (!this.asyncRender) {
        return true
      }
      const oldList = this.oldList.find((old) => {
        const oldVal = _.cloneDeep(old)
        delete oldVal.isActive
        const newVal = _.cloneDeep(tList)
        delete newVal.isActive
        return compare.compare(oldVal, newVal)
      })
      if (tList.isActive && (oldList || {}).isActive) {
        return true
      }
      if ((x.$index < this.renderingRowCount)) {
        return true
      } else {
        return false
      }
    },
    rowCountUp () {
      setTimeout(() => {
        this.renderingRowCount += 5
        if (this.renderingRowCount < 100) {
          this.rowCountUp()
        }
      }, 1)
    },
    isShow (column) {
      const isShow = typeof column.isShow === 'function' ? column.isShow(column) : column.isShow
      if (isShow === false) {
        return false
      }

      // isAvailableにより、表示対象外となっている場合、非表示。設定がない場合は表示
      if (('isAvailable' in column) && !column.isAvailable) {
        return false
      }

      return !column.funcId || this.$isFunctionShown(column.funcId)
    },
    isDisplay (column) {
      // isActiveにより、表示対象外となっている場合、非表示。設定がない場合は表示
      if (('isActive' in column) && !column.isActive) {
        return false
      }
      return true
    },
    minColumn (column) {
      return column.width ? null : (this.isDisplay(column) ? this.minColumnWidth : null)
    },
    label (column) {
      return column.hasSearcher ? '' : column.label
    },
    sortable (obj) {
      const name = this.getPropertyName(obj)
      const enableSort = obj.sortable && (
        name !== '' ||
        typeof obj.sortMethod === 'function' ||
        typeof obj.sortBy === 'function' ||
        (typeof obj.sortBy === 'string' && obj.sortBy !== '') ||
        Array.isArray(obj.sortBy)
      )
      return enableSort ? obj.sortable : false
    },
    sortMethod (obj) {
      if (typeof obj.sortMethod === 'function') {
        return obj.sortMethod
      }
    },
    filters (obj) {
      if (typeof obj.filters === 'function') {
        return obj.filters()
      } else if (Array.isArray(obj.filters)) {
        return obj.filters
      }
    },
    filterMethod (obj) {
      if (typeof obj.filterMethod === 'function') {
        return obj.filterMethod
      }
    },
    getPropertyName (obj) {
      return obj.name || (typeof obj.path === 'string' ? obj.path : '')
    },
    getColumnKey (obj) {
      return obj.columnKey || ''
    },
    onRowClick (row, column, event) {
      if (_.get(column, 'type') === 'selection') return
      if (window.getSelection().isCollapsed) {
        // 文字列が選択されていない場合、行クリックイベントを配信する.
        this.$emit('row-click', row, column, event)
      }
    },
    onRowDoubleClick (row, column, event) {
      this.$emit('row-dblclick', row, column, event)
    },
    onSortChange ({ column, prop, order }) {
      this.$emit('sort-change', { column, prop, order })
    },
    getUuid (row) {
      if (!row.tableCellKey) {
        row.tableCellKey = uuid()
      }
      return row.tableCellKey
    },
    canSelectRow (row, index) {
      return typeof this.canSelectMethod === 'function' ? this.canSelectMethod(row, index) : true
    },
    // 全選択 または 全解除クリック時イベント
    onSelectAll (rows) {
      const allData = _.get(this.$store.state, `${this.storePath}.tdLists`, []).filter((row) => _.get(row, 'canSelect', true))
      const selectedData = this.$refs.table.selection
      const tableHeaderChkbx = this.$refs.table.$el.querySelectorAll('th .is-checked')
      let isSelectAll = tableHeaderChkbx.length === 0
      if (isSelectAll) {
        // 全選択
        allData.forEach((row) => {
          if (!selectedData.some((selectedRow) => _.get(row, this.rowKey, 0) === _.get(selectedRow, this.rowKey, 1))) {
            this.$refs.table.toggleRowSelection(row)
          }
        })
      } else {
        // 全解除
        this.$refs.table.clearSelection()
      }
    },
    // チェックボックス変更時イベント
    //  ・ 全チェックのチェックボックスの調整
    //    ヘッダーのチェックボックスは表示している中で全チェックであれば、チェックされてしまうので、ここで調整する
    adjustmentAllSelectCheckBox (rows) {
      if (!this.showSelection) return
      setTimeout(() => {
        const allData = _.get(this.$store.state, `${this.storePath}.tdLists`, []).filter((row) => _.get(row, 'canSelect', true))
        const selectedData = this.$refs.table.selection
        let isAll = false
        if (allData.length === selectedData.length) {
          isAll = true
        }
        if (isAll) {
          // チェックを入れる
          let table = this.$refs.table.$el.querySelectorAll('th .el-checkbox')
          for (const obj of table) {
            obj.classList.add('is-checked')
            obj.setAttribute('aria-checked', true)
          }
          table = this.$refs.table.$el.querySelectorAll('th .el-checkbox__input')
          for (const obj of table) {
            obj.classList.add('is-checked')
            obj.classList.remove('is-indeterminate')
            obj.setAttribute('aria-checked', true)
          }
        } else {
          // チェックを抜く
          let table = this.$refs.table.$el.querySelectorAll('th .is-checked')
          for (const obj of table) {
            obj.classList.remove('is-checked')
            obj.removeAttribute('aria-checked')
          }
        }
      }, 2)
    }
  }
}
</script>
